import { Link, router, usePage } from '@inertiajs/react'
import { useRef, useState } from 'react'
import { useI18n } from '../../lib/useI18n'
import Icon from './Icon'
import Popover from './Popover'

// Avatar initials: prefer the name (first + last word's initials, or the first
// two letters of a single-word name), else the email's local part (the bit
// before "@") — which yields just one letter when the local part is a single
// character. Falls back to "G4" when neither is present.
function avatarInitials(name?: string | null, email?: string | null): string {
  const trimmed = name?.trim()
  if (trimmed) {
    const words = trimmed.split(/\s+/)
    const initials =
      words.length > 1 ? words[0][0] + words[words.length - 1][0] : words[0].slice(0, 2)
    return initials.toUpperCase()
  }
  const local = email?.split('@')[0] ?? ''
  return local ? local.slice(0, 2).toUpperCase() : 'G4'
}

// Avatar + account dropdown for the authenticated shells (operator + portal).
// Profile header, Account, Security & 2FA, and Log out. (Theme + language live in
// the top bar, so they're not duplicated here.) The /account, /security and
// /logout paths exist on both surfaces (host-routed). The portal also surfaces a
// standalone Security entry; the operator's Account hub already links out to it.
// Keyed off the Inertia page component prefix.
export default function UserMenu() {
  const { t } = useI18n()
  const page = usePage<{ current_user?: { name?: string | null; email?: string } | null }>()
  const { props } = page
  const isOperator = page.component?.startsWith('operator/') ?? false
  const user = props.current_user
  const initials = avatarInitials(user?.name, user?.email)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)

  const itemClass =
    'flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-ink hover:bg-surface-2'

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('app.user_menu.open')}
        title={user?.email}
        className="grid h-9 w-9 flex-none place-items-center rounded-[10px] bg-accent font-display text-sm font-bold text-accent-ink"
      >
        {initials}
      </button>

      <Popover
        anchorRef={anchorRef}
        open={open}
        onClose={() => setOpen(false)}
        className="w-64 overflow-hidden rounded-[12px] border border-border bg-surface py-1 shadow-[var(--shadow-lg,var(--shadow))]"
      >
        <div role="menu">
          <div className="px-3 py-2.5">
            <div className="truncate text-sm font-semibold text-ink">{user?.name ?? '—'}</div>
            <div className="truncate text-xs text-ink-muted">{user?.email}</div>
          </div>

          <div className="border-t border-border" />

          {isOperator ? (
            // Operator: the personal Account hub is enough — the admin Settings
            // grid lives in the sidebar, and Account links out to security/2FA.
            <Link
              href="/account"
              role="menuitem"
              className={itemClass}
              onClick={() => setOpen(false)}
            >
              <Icon name="user" size={16} className="text-ink-2" /> {t('app.user_menu.account')}
            </Link>
          ) : (
            // Portal: the personal Account hub lives at /account, plus a direct
            // Security entry.
            <>
              <Link
                href="/account"
                role="menuitem"
                className={itemClass}
                onClick={() => setOpen(false)}
              >
                <Icon name="user" size={16} className="text-ink-2" /> {t('app.user_menu.account')}
              </Link>
              <Link
                href="/security"
                role="menuitem"
                className={itemClass}
                onClick={() => setOpen(false)}
              >
                <Icon name="shield-check" size={16} className="text-ink-2" />{' '}
                {t('app.user_menu.security')}
              </Link>
            </>
          )}

          <div className="border-t border-border" />

          <button
            type="button"
            role="menuitem"
            onClick={() => router.delete('/logout')}
            className={`${itemClass} font-semibold text-accent`}
          >
            <Icon name="lock" size={16} /> {t('app.user_menu.logout')}
          </button>
        </div>
      </Popover>
    </>
  )
}
